import {
	ReactElement,
	Ref,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { useActiveSettingsMenuItem } from "../../SidebarMenu/sidebarMenuSelectionStore";
import { SettingsPage } from "../../SettingsPage";
import { localize } from "client_legacy/ndaw/culture";
import {
	computeContextMenuTitle,
	ContextMenu,
	FloatingActionButton,
	Pagination,
	SearchInput,
	SearchPlaceholder,
	TableView,
	TableViewNoResultConfig,
	ToolbarAction,
	ToolbarRegularAction,
	useConfirmationDialog,
	useContextMenu
} from "client/Components";
import {
	ActionKind,
	buildActionsForTopBar,
	deselectAllTitle
} from "client_legacy/ndaw/actions";
import {
	AfasConnectionListItemModel,
	deleteMultiple,
	list
} from "client/Api/AfasConfiguration/afasConfiguration";
import { AfasConnectionDialog } from "./AfasConnectionDialog";
import { DataSourceSortDirection, DataSourceSortableState } from "client/Data/sorting";
import {
	AlertManager,
	createLocalDataSource,
	MediaQueryKind,
	useAlerts,
	useSelection,
	useSortHandler,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { contains } from "client/Data/searching";
import { handlePageDataLoadingError } from "client/Features/ErrorPage/errorStore";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";
import { resolveFormattedTextString } from "client/Misc/utils";

type ConfigureConnectionState = {
	isVisible: boolean;
	connectionId?: number;
};

const useAfasConnectionDataSource =
	createLocalDataSource<AfasConnectionListItemModel>()
		.setConfiguration({
			search: {
				doesMatchItem: searchMatchFunction,
			},
			hasPagination: true
		})
		.getHook();

export const AfasConnectionPage = (): ReactElement => {
	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [allItems, setAllItems] = useState<readonly AfasConnectionListItemModel[]>([]);
	const [selectedItems, setSelectedItems] = useState<ReadonlyArray<AfasConnectionListItemModel>>([]);
	const [currentPageNumber, setCurrentPageNumber] = useState<number>(0);
	const [searchTerm, setSearchTerm] = useState<string>("");
	const bodyRef: Ref<HTMLDivElement> = useRef<HTMLDivElement>(null);
	const [contextMenuHandler, contextMenuProps] = useContextMenu(bodyRef);
	const alertManager: AlertManager = useAlerts();

	useActiveSettingsMenuItem(SettingsPageKind.Afas);

	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();
	const [sortingState, setSortingState] = useState<DataSourceSortableState<AfasConnectionListItemModel>>(
		{ propertyName: "name", direction: DataSourceSortDirection.Ascending }
	);
	const { sortingFor } = useSortHandler<AfasConnectionListItemModel>(sortingState, setSortingState);
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const [configureDialogState, setConfigureDialogState] = useState<ConfigureConnectionState>({
		isVisible: false
	});

	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

	useEffect(
		(): (() => void) => {
			async function load(signal: AbortSignal): Promise<void> {
				setIsBusy(true);

				try {
					const items: readonly AfasConnectionListItemModel[] = await list(signal)
						.catch(handlePageDataLoadingError);

					setAllItems(items);
					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					setIsBusy(false);
				}
			}

			const cancellation: AbortController = new AbortController();
			load(cancellation.signal);

			return () => cancellation.abort();
		},
		[alertManager]
	);

	const deleteHandler = useCallback(
		async (items: readonly AfasConnectionListItemModel[]): Promise<void> => {
			const result = await openConfirmationDialog({
				title: items.length === 1
					? localize("_AreYouSureYouWantToDeleteThisConnection")
					: localize("_AreYouSureYouWantToDeleteTheseConnections"),
				confirmText: localize("_Delete"),
				trackingId: "delete-afas-connection-confirmation-dialog"
			});

			if (!result.wasSuccessful) {
				return;
			}

			setIsBusy(true);

			try {
				await deleteMultiple(
					items.map(i => i.id),
					globalCancellation
				);

				setAllItems(
					x => x.filter(
						i => !items.some(di => i.id === di.id)
					)
				);

				setIsBusy(false);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}
				console.error(e);
				alertManager.showError(localize("_ErrorDeletingConnections"));
				setIsBusy(false);
			}
		},
		[
			alertManager,
			openConfirmationDialog,
			globalCancellation
		]
	);

	const { currentItems, pagination } = useAfasConnectionDataSource(
		allItems, {
		sorting: sortingState,
		pagination: {
			currentPageNumber,
			amountPerPage: 25
		},
		searchTerm
	});

	const { multiSelection, selectionFor } = useSelection(
		currentItems,
		"id",
		{
			isMultiSelect: true,
			selectedItems,
			setSelectedItems
		}
	);

	const actionContext = useMemo(
		(): ConnectionActionContext => ({
			selection: selectedItems,
			isMobile
		}),
		[isMobile, selectedItems]
	);

	const selectionActions: readonly ConnectionAction[] = useMemo(
		() => getSelectionActions({
			deselectHandler: () => setSelectedItems([])
		}),
		[]
	);

	const contextualActions: readonly ConnectionAction[] = useMemo(
		() => listContextualActions({
			editHandler: (item) => {
				setConfigureDialogState({ isVisible: true, connectionId: item.id });
			},
			deleteHandler
		}),
		[deleteHandler]
	);

	const mainAction = useMemo(
		(): ToolbarRegularAction<ConnectionActionContext> => {
			return getMainAction(
				() => setConfigureDialogState({isVisible: true})
			);
		},
		[]
	);

	const hasSelection: boolean = actionContext.selection
		.length !== 0;

	const topBarActions = useMemo(
		(): readonly ConnectionAction[] => {
			return buildActionsForTopBar<ConnectionActionContext, ConnectionAction>(
				{
					mainActions: [mainAction],
					selectionActions,
					contextualActions
				},
				hasSelection,
				isResponsive,
				false
			);
		},
		[
			hasSelection,
			contextualActions,
			isResponsive,
			mainAction,
			selectionActions
		]
	);

	const afterSaveHandler = (item: AfasConnectionListItemModel): void => {
		setAllItems(allItems => {
			const existingItemIndex: number = allItems.findIndex(i => i.id === item.id);
			const copy: AfasConnectionListItemModel[] = [...allItems];

			if (existingItemIndex === -1) {
				copy.push(item);
			} else {
				copy.splice(existingItemIndex, 1, item);
			}

			return copy;
		});
	};

	const noResultConfig = useMemo(
		(): TableViewNoResultConfig => {
			if (searchTerm) {
				return {
					icon: "magnifying-glass",
					title: localize("_NoSearchResults"),
					message: localize("_PleaseTrySearchingWithAnotherTerm")
				};
			}

			return {
				icon: "gear",
				title: resolveFormattedTextString({
					template: localize("_NoXIntegrationConnectionsYet"),
					replacements: [
						{
							what: "{{IntegrationName}}",
							filler: localize("_Afas")
						}
					]
				}),
				message: resolveFormattedTextString({
					template: localize("_ThereAreNoXIntegrationConnectionsYet"),
					replacements: [
						{
							what: "{{IntegrationName}}",
							filler: localize("_Afas")
						}
					]
				})
			};
		},
		[searchTerm]
	);

	return (
		<>
			<SettingsPage
				hasBusyIndicator={isBusy}
				actions={topBarActions}
				actionContext={actionContext}
				title={localize("_AfasConnections")}
				hasSelection={hasSelection}
			>
				<SettingsPage.Section
					height="stretch"
				>
					<SettingsPage.SearchSection>
						<SearchInput
							onChange={setSearchTerm}
							icon="filter"
							height="large"
							placeholder={(
								<SearchPlaceholder
									kind="filter"
									properties={[localize("_Name")]}
								/>
							)}
						/>
					</SettingsPage.SearchSection>

					<SettingsPage.Section height="stretch">
						<TableView
							multiSelectable={multiSelection}
							items={currentItems}
							itemKey="id"
							height="stretch"
							trackingId="afas-connections"
							columns={[
								{
									title: localize("_Name"),
									sortable: sortingFor("name"),
									trackingId: "name"
								}
							]}
							ref={bodyRef}
							noResultConfig={noResultConfig}
						>
							{
								item => (
									<TableView.Row
										selectable={selectionFor(item)}
										key={item.id}
										onExecute={() => setConfigureDialogState({ isVisible: true, connectionId: item.id })}
										onContextMenu={contextMenuHandler}
										trackingId={item.id.toString()}
									>
										<TableView.Cell>{item.name}</TableView.Cell>
									</TableView.Row>
								)
							}
						</TableView>
						<Pagination {...pagination} onPageNumberChanged={setCurrentPageNumber} />
						{ contextMenuProps && (
							<ContextMenu<ConnectionActionContext>
								{... contextMenuProps}
								actions={contextualActions}
								context={actionContext}
								trackingId="afas-connection-overview-context-menu"
								title={computeContextMenuTitle(
									actionContext.selection,
									(x) => x.name
								)}
							/>
						)}
					</SettingsPage.Section>
				</SettingsPage.Section>
				{mainAction.canExecute(actionContext) && (
					<>
						<div className="preserve-space-for-mobile-plus-button"/>
						<FloatingActionButton
							onClick={() => mainAction.execute(actionContext)}
							icon="plus"
							trackingId="new-button"
						/>
					</>
				)}
				<AiAssistantFabSpacing />
			</SettingsPage>
			{configureDialogState.isVisible && (
				<AfasConnectionDialog
					connectionId={configureDialogState.connectionId}
					onClose={() => setConfigureDialogState({isVisible: false})}
					onAfterSave={afterSaveHandler}
				/>
			)}
			{confirmationDialog}
		</>
	);
};
AfasConnectionPage.displayAfasConnectionPage = "AfasConnectionPage";

function searchMatchFunction(
	item: AfasConnectionListItemModel,
	normalizedNotEmptySearchTerm: Exclude<string, "">
): boolean {
	const {
		name
	} = item;

	if (contains(name, normalizedNotEmptySearchTerm)) {
		return true;
	}

	return false;
}

type ConnectionActionContext = {
	selection: readonly AfasConnectionListItemModel[];
	isMobile: boolean;
};

type ConnectionAction = ToolbarAction<ConnectionActionContext>;

type ContextualActionsHandlers = {
	editHandler(item: AfasConnectionListItemModel): void;
	deleteHandler(items: readonly AfasConnectionListItemModel[]): void;
};

function listContextualActions(handlers: ContextualActionsHandlers): readonly ConnectionAction[] {
	const {
		editHandler,
		deleteHandler
	} = handlers;

	return [
		{
			kind: ActionKind.Regular,
			name: "edit",
			title: localize("_Edit"),
			canExecute: (c) => c.selection.length === 1,
			execute: (c) => editHandler(c.selection[0]),
			icon: "pen"
		},
		{
			kind: ActionKind.Regular,
			name: "delete",
			title: localize("_Delete"),
			canExecute: (c) => c.selection.length > 0,
			execute: (c) => deleteHandler(c.selection),
			icon: "trash-can"
		}
	];
}

type SelectionActionsHandlers = {
	deselectHandler: () => void;
};

function getSelectionActions(handlers: SelectionActionsHandlers): readonly ConnectionAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "deselectAll",
			title: (c) => deselectAllTitle(c.selection),
			icon: "circle-xmark",
			canExecute: (c) => c.selection.length > 0,
			execute: () => handlers.deselectHandler()
		},
		{
			kind: ActionKind.Divider,
			name: "deselect-all-divider",
			canExecute: (c) => !c.isMobile
				&& c.selection.length > 0
		}
	];
}

function getMainAction(
	newHandler: () => void
): ToolbarRegularAction<ConnectionActionContext> {
	return {
		kind: ActionKind.Regular,
		name: "createNew",
		title: localize("_New"),
		canExecute: () => true,
		execute: () => newHandler(),
		icon: "plus",
		isPrimary: true
	};
}

export const afasConfigurationRouting = {
	page: "/AfasConfiguration"
};